import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import { cached, tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { APIData, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';
import { replaceTextLinksWithHtml } from 'webapp/utils/html';

import type { CustomQuery } from 'webapp/models-custom';
import Serviced from 'webapp/models-custom/serviced';
import type Source from 'webapp/models-custom/source';

export const enum StatusObjectType {
	SYNC = 'sync',
	MEDIA_SYNC = 'media_sync', // === media creatives
	CONNECTION = 'connection', // === source
	GRANULATION = 'granulation', // === invalid granulation
	DATASET_PARTIAL_SYNC = 'dataset_partial_sync', // === dataset partial sync running
	FIRST_SYNC = 'first_sync', // === first sync running, no data on the block yet
	FILTER_NOT_APPLIED = 'filter_not_applied', // === Horizon: master/block filter doesn't fit the metric, so it is read without those filters
	FILTERS_NOT_APPLICABLE = 'filters_not_applicable', // === client-side only: pre-built metric can't apply its filters
	SELECTION = 'selection', // === invalid metric setup: DATASETS, e.g. filter column not in the dataset schema; Horizon, breakdown not available
}

export const enum StatusObjectAction {
	RECONNECT = 'reconnect', // source (connection) only
	REAUTHORIZE = 'reauthorize', // source (connection) only
	SELECT = 'select', // source (connection) only
	VERSION = 'version', // source (connection) only
	BILLING = 'billing', // source (connection) only
	REFETCH = 'refetch', // others
	SOURCE_CONNECTED = 'source_connected',
	//DATASETS only
	FINISH_SETUP = 'finish_setup',
	EDIT = 'edit',
	SOURCE_SETUP = 'source_setup',
	UPGRADE_PLAN = 'upgrade_plan',
}

export const enum StatusIconColor {
	DEFAULT = 'default',
	RED = 'red',
	ORANGE = 'orange',
}

export interface StatusObjectExtra {
	sourceId: number;
	sourceTypeKey: string;
	sourceTitle: string;
	connectionId?: string;
}

interface DataForStatusObject {
	level: string;
	title: string;
	description: string;
	action: StatusObjectAction;
	userResponseLink: string;
	type?: StatusObjectType;
	extra?: StatusObjectExtra | null;
}

// a block's data select, for naming the dataset metric its status objects belong to
export interface StatusObjectOwner {
	statusObjects: StatusObject[];
	datasetMetricLabel: string; // empty when not a dataset metric
}

export default class StatusObject extends Serviced {
	@service('account') declare accountService: Service.Account;
	@service('connect') declare connectService: Service.Connect;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('logger') declare loggerService: Service.Logger;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('billing') declare billingService: Service.Billing;

	@tracked type: StatusObjectType | null;
	@tracked level; // SOURCES: 'error', 'onHold' (?)
	@tracked title: string;
	@tracked description: string;
	@tracked action: StatusObjectAction;
	@tracked userResponseLink: string;
	@tracked source?: Source;

	// for all EXECEPT SOURCES
	@tracked queryId: number | null = null;
	@tracked blockId: number | null = null;

	dataLocation = ''; // from data - 'series' or 'number'

	// sources only
	sourceTitle;

	constructor(data: DataForStatusObject) {
		super();

		this.type = data.type || null;
		this.level = data.level;
		this.title = data.title;
		this.description = data.description;
		this.action = data.action;
		this.userResponseLink = this.accountService.hideHelpArticles ? '' : data.userResponseLink;

		const extraData = data.extra;
		if (extraData) {
			if (this.isSource) {
				this.sourceTitle = extraData.sourceTitle;
				this.source = this.mmSourcesService.sourceModels.find(
					(source) => source.numericId === extraData.sourceId,
				);

				if (this.isStatusActionable) {
					const connectFn = (reauthorize = false) => {
						void this.connectService.connectSourceFromIdOrKey(extraData.sourceTypeKey, {
							sourceId: extraData.sourceId,
							reauthorize,
						});
					};
					this._triggerActionReconnect = () => {
						connectFn(false);
					};
					this._triggerActionReauthorize = () => {
						connectFn(true);
					};
					this._triggerActionSourceSelected = () => {
						void this.connectService.connectSourceFromIdOrKey(extraData.sourceTypeKey, {
							sourceId: extraData.sourceId,
							connectionId: extraData.connectionId,
							sourceSelected: true,
						});
					};
				}
			}
		}
	}

	get isError() {
		return this.level === 'error';
	}

	get isInfo() {
		return this.level === 'info';
	}

	get isOnHold() {
		return this.level === 'onHold';
	}

	get isSource() {
		return this.type === StatusObjectType.CONNECTION;
	}

	get isSync() {
		return this.type === StatusObjectType.SYNC;
	}

	// when media creatives images are loading
	get isMediaSync() {
		return this.type === StatusObjectType.MEDIA_SYNC;
	}

	get isGranulation() {
		return this.type === StatusObjectType.GRANULATION;
	}

	get isDatasetPartialSync() {
		return this.type === StatusObjectType.DATASET_PARTIAL_SYNC;
	}

	// A metric collecting its data for the first time. Not an error: there is nothing wrong and nothing for
	// the user to do, the block just has no data yet and will fill in on its own.
	get isFirstSync() {
		return this.type === StatusObjectType.FIRST_SYNC;
	}

	get isFilterNotApplied() {
		return this.type === StatusObjectType.FILTER_NOT_APPLIED;
	}

	get isFiltersNotApplicable() {
		return this.type === StatusObjectType.FILTERS_NOT_APPLICABLE;
	}

	get isSelection() {
		return this.type === StatusObjectType.SELECTION;
	}

	get canResolveActionableStatus() {
		if ([StatusObjectAction.RECONNECT, StatusObjectAction.REAUTHORIZE].includes(this.action)) {
			return (
				!this.source?.isConnectionPrivate &&
				((this.action === StatusObjectAction.RECONNECT && this.canReconnect) ||
					(this.action === StatusObjectAction.REAUTHORIZE && this.canReauthorize))
			);
		} else if (this.isDatasetError) {
			// DATASETS only (only actionable in data manager)
			return false;
		} else {
			return true;
		}
	}

	@cached
	get statusIconClass(): IconName {
		let icon: IconName;
		if (this.isFirstSync) {
			// Neither an alert nor a tick: nothing is wrong, and nothing has finished either.
			icon = 'InfoFill';
		} else if (
			this.isError ||
			this.isOnHold ||
			this.isDatasetError ||
			this.isFilterNotApplied ||
			this.isFiltersNotApplicable
		) {
			icon = 'AlertFill';
		} else {
			icon = 'Check';
		}
		return icon;
	}

	get statusIconColor(): StatusIconColor {
		return this.isGranulation ||
			this.isDatasetPartialSync ||
			this.isFirstSync ||
			this.isFilterNotApplied ||
			this.isFiltersNotApplicable ||
			(this.isSync && this.isInfo)
			? StatusIconColor.ORANGE
			: StatusIconColor.RED;
	}

	get isStatusActionable() {
		return (
			!this.navigationService.isDatawallApp && // skip actions on public datawall
			(this.canReconnect ||
				this.canReauthorize ||
				this.isActionBilling ||
				this.isActionRefetch ||
				this.isActionSourceConnected ||
				this.isActionUpgradePlan)
		);
	}

	get isDatasetError() {
		return this.isActionEdit || this.isActionSourceSetup || this.isActionFinishSetup;
	}

	get canReconnect() {
		return (
			StatusObject.canReconnect(this.action) && // used for sources only
			!!this.sourceTitle
		);
	}

	get canReauthorize() {
		return this.action === StatusObjectAction.REAUTHORIZE; // used for sources only
	}

	get isActionFinishSetup() {
		return this.action === StatusObjectAction.FINISH_SETUP;
	}

	get isActionVersion() {
		return this.action === StatusObjectAction.VERSION;
	}

	get isActionBilling() {
		return this.action === StatusObjectAction.BILLING;
	}

	get isActionRefetch() {
		return this.action === StatusObjectAction.REFETCH;
	}

	get isActionSourceConnected() {
		return this.action === StatusObjectAction.SOURCE_CONNECTED;
	}

	get isActionEdit() {
		return this.action === StatusObjectAction.EDIT;
	}

	get isActionSourceSetup() {
		return this.action === StatusObjectAction.SOURCE_SETUP;
	}

	get isActionUpgradePlan() {
		return this.action === StatusObjectAction.UPGRADE_PLAN;
	}

	get statusActionCapitalized() {
		const action = this.action || '';
		let string = '';
		if (action === StatusObjectAction.VERSION) {
			string = StatusObjectAction.RECONNECT;
		} else if (action === StatusObjectAction.SOURCE_CONNECTED) {
			string = 'Setup source';
		} else if (action === StatusObjectAction.UPGRADE_PLAN) {
			string = 'Upgrade plan';
		} else {
			string = action;
		}
		return capitalize(string);
	}

	@cached
	get userResponseLinkFormatted() {
		return this.userResponseLink
			? `<a class="learn-more" href="${this.userResponseLink}" target="_blank">Learn more</a>`
			: '';
	}

	@cached
	get descriptionWithLinks() {
		return replaceTextLinksWithHtml(this.description) + ' ' + this.userResponseLinkFormatted;
	}

	@cached
	get statusTitleAndDescription() {
		let text = `<strong>${this.title}</strong><br>`;

		const desc = this.descriptionWithLinks;
		if (desc) {
			text += `<p>${desc}</p>`;
		}
		return text;
	}

	@cached
	get statusTitleAndDescriptionWithAction() {
		const text = this.statusTitleAndDescription;

		if (this.isStatusActionable || this.isDatasetError) {
			return this.getStatusActionElement(text);
		}

		return $(text);
	}

	getStatusActionHTML(text = '') {
		// adding data-type attribute for use on preview data error with status object action refetch in CustomMetricSetupFormPrebuilt
		return `${text}<a class="link link--green link--underline actionable" data-type="${this.action}" role="button">${this.canResolveActionableStatus ? this.statusActionCapitalized : 'View in Data manager'}</a>`;
	}

	getStatusActionElement(text = '') {
		const newText = this.getStatusActionHTML(text);

		const el = $(`<div class="wrapper">${newText}</div>`);
		el.find('a.actionable').on('click', () => {
			this.triggerAction();
			return false;
		});

		return el;
	}

	isEqual(statusObject: StatusObject, ignoreLocation = false) {
		return (
			this.type === statusObject.type &&
			this.level === statusObject.level &&
			this.title === statusObject.title &&
			this.description === statusObject.description &&
			this.action === statusObject.action &&
			this.userResponseLink === statusObject.userResponseLink &&
			(ignoreLocation || this.dataLocation === statusObject.dataLocation)
		);
	}

	triggerAction() {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.canResolveActionableStatus) {
			this.navigationService.transitionToRoute('datamanager.connected');
			return;
		}

		if (this.canReconnect) {
			this._triggerActionReconnect();
		} else if (this.canReauthorize) {
			this._triggerActionReauthorize();
		} else if (this.isActionBilling) {
			this._triggerActionBilling();
		} else if (this.isActionSourceConnected) {
			this._triggerActionSourceSelected();
		} else if (this.isActionRefetch) {
			if (this.queryId || this.blockId) {
				void this.triggerActionRefetch();
			} else {
				this.infolayerService.error(
					'Refetch is currently not possible. In case this error persists, report it to our technical support.',
				);
				this._logError('Refetch with no queryId / blockId');
			}
		} else if (this.isActionUpgradePlan) {
			this._triggerActionUpgradePlan();
		}
	}

	private triggerActionRefetch() {
		let payload;

		if (this.blockId) {
			payload = { blockId: this.blockId };
		} else if (this.queryId) {
			payload = { queryId: this.queryId };
		}

		if (!payload) {
			this.loggerService.logError('[StatusObject] Resync without id');
			return;
		}

		return this.mmMetricsService
			.requestDataRefetch(payload)
			.then(() => {
				this.infolayerService.ok('Resync triggered!');
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	_triggerActionBilling() {
		this.navigationService.transitionToRoute('settings.billing');
	}

	_triggerActionReconnect = () => {
		// overwritten, just for sources (check `createFromSource`)
	};

	_triggerActionReauthorize = () => {
		// overwritten, just for sources (check `createFromSource`)
	};

	_triggerActionSourceSelected = () => {
		// overwritten, just for sources (check `createFromSource`)
	};

	_triggerActionUpgradePlan = () => {
		const targetPlan = this.billingService.getNextPlanForFeatureWithSameBillingCycle(
			BillingFeature.ADVANCED_DATA_SOURCES,
		);
		this.billingService.switchToExpiredPromotionPlan(
			'datasets_trial_expired',
			targetPlan || undefined,
		);
	};

	_logError(msg: string) {
		const statusObject = {
			type: this.type,
			title: this.title,
			description: this.description,
			action: this.action,
			level: this.level,
			userResponseLink: this.userResponseLink,
		};
		this.loggerService.logError(`[StatusObject] ${msg}`, {
			statusObject: JSON.stringify(statusObject),
		});
	}

	// for _Issue logging START

	get shouldLogEventIssue() {
		const dontLogTitles = ['Deprecated Metric'];
		return (
			!this.isFilterNotApplied &&
			!this.isFiltersNotApplicable &&
			!dontLogTitles.some((t) => this.title.includes(t))
		);
	}

	// for _Issue logging END

	static canReconnect(statusAction: StatusObjectAction) {
		return [
			StatusObjectAction.RECONNECT,
			StatusObjectAction.SELECT,
			StatusObjectAction.VERSION,
		].includes(statusAction);
	}

	static getDataErrorText(statusObjects: StatusObject[] = [] /*, addIntroText = true*/) {
		const partials = statusObjects.map((statusObject) => {
			const el = statusObject.statusTitleAndDescriptionWithAction.clone(true);
			if (statusObject.isSource) {
				el.prepend(
					`The data source "${statusObject.sourceTitle}" reports the following problem: `,
				);
			}
			return el;
		});

		let text: JQuery<HTMLElement> | null = null;
		if (partials.length) {
			const wrapperEl = $('<div></div>');

			// if (addIntroText) {
			// 	wrapperEl.append('<strong>Your data might not be up to date.</strong><br />');
			// }

			partials.forEach((el) => {
				wrapperEl.append(el);
				// wrapperEl.append('<br />');
			});

			text = wrapperEl;
		}

		return text;
	}

	static createFromData(data: APIData.StatusObject) {
		// generic
		const statusObject = new StatusObject({
			type: data.type ?? undefined,
			level: data.level,
			title: data.title || '',
			description: data.description || '',
			// both can be missing (e.g. client-side statuses): then no action check matches
			action: data.userAction as StatusObjectAction,
			userResponseLink: data.userResponseLink!,
			extra: data.extra,
		});
		return statusObject;
	}

	static createFromDataArray(dataArray?: APIData.StatusObject[]) {
		if (!dataArray) {
			dataArray = [];
		}

		if (!dataArray.map) {
			// not an array?
			dataArray = [];
		}

		return dataArray.map(this.createFromData);
	}

	static createFromSource(source: Source) {
		this.checkToLogConnectionTypeError(
			source.statusType,
			// 	'createFromSource', {
			// 	sourceId: source.id,
			// 	statusType: source.statusType,
			// 	statusLevel: source.statusLevel,
			// 	statusTitle: source.statusTitle,
			// 	statusDescription: source.statusDescription,
			// 	statusAction: source.statusAction,
			// 	statusUserResponseLink: source.statusUserResponseLink,
			// }
		);

		return this.createFromData({
			type: StatusObjectType.CONNECTION,
			level: source.statusLevel,
			title: source.statusTitle,
			description: source.statusDescription,
			userAction: source.statusAction,
			userResponseLink: source.statusUserResponseLink,
			extra: this.getConnectionExtra(source),
		});
	}

	static createFromCustomQuery(customQuery: CustomQuery) {
		const { sourceObj } = customQuery;
		const isStatusObjectTypeConnection = this.checkToLogConnectionTypeError(
			customQuery.statusType,
			// 'createFromCustomQuery',
			// {
			// 	customQueryId: customQuery.id,
			// 	statusType: customQuery.statusType,
			// 	statusLevel: customQuery.statusLevel,
			// 	statusTitle: customQuery.statusTitle,
			// 	statusDescription: customQuery.statusDescription,
			// 	statusAction: customQuery.statusAction,
			// 	statusUserResponseLink: customQuery.statusUserResponseLink,
			// },
		);

		return this.createFromData({
			type: customQuery.statusType,
			level: customQuery.statusLevel,
			title: customQuery.statusTitle,
			description: customQuery.statusDescription,
			userAction: customQuery.statusAction,
			userResponseLink: customQuery.statusUserResponseLink,
			extra:
				isStatusObjectTypeConnection && sourceObj
					? this.getConnectionExtra(sourceObj)
					: undefined,
		});
	}

	static eliminateDuplicates(allStatusObjects: StatusObject[], ignoreLocation = false) {
		const statusObjects: StatusObject[] = [];
		allStatusObjects.forEach((statusObject) => {
			const hasDuplicate = statusObjects.some((so) => {
				return so !== statusObject && so.isEqual(statusObject, ignoreLocation);
			});
			if (!hasDuplicate) {
				statusObjects.push(statusObject);
			}
		});
		return statusObjects;
	}

	// DATASETS: the backend message names neither the metric nor the dataset, so we name them in the data status popup
	static getDatasetMetricLabel(metricName?: string | null, datasetName?: string | null): string {
		const parts: string[] = [];
		if (metricName) {
			parts.push(`Metric: ${metricName}`);
		}
		if (datasetName) {
			parts.push(`Dataset: ${datasetName}`);
		}
		return parts.join(' · ');
	}

	// A status object belongs to the data selects that report it (or an equal one, removed by eliminateDuplicates).
	// It is named only when those are one dataset metric; one no data select reports gets no label rather than a guess.
	static getDatasetMetricLabels(
		statusObjects: StatusObject[],
		dataSelects: StatusObjectOwner[],
	): Map<StatusObject, string> {
		const labels = new Map<StatusObject, string>();
		statusObjects.forEach((statusObject) => {
			const owners = dataSelects.filter((ds) =>
				ds.statusObjects.some((so) => so.isEqual(statusObject, true)),
			);
			const ownerLabels = uniq(owners.map((ds) => ds.datasetMetricLabel));
			const label = ownerLabels.length === 1 ? ownerLabels[0]! : '';

			if (label) {
				labels.set(statusObject, label);
			}
		});
		return labels;
	}

	private static checkToLogConnectionTypeError(
		statusType: StatusObjectType | '' | null | undefined,
		// caller: string,
		// context: Record<string, unknown>,
	) {
		const isConnection = statusType === StatusObjectType.CONNECTION;

		// TODO: We receive many different unexpected statusType values ('request', '' (empty string), etc.)
		// as well as statusLevel 'active' with statusTitle 'OK'. We should investigate why these non-connection
		// types are being passed to source/customQuery creation methods and handle them appropriately.
		// if (!isConnection) {
		// 	const loggerService = Serviced.appInstance.lookup('service:logger');
		// 	loggerService.logError(
		// 		`[StatusObject.${caller}] StatusObject is not a connection type`,
		// 		context,
		// 		Severity.WARNING,
		// 	);
		// }

		return isConnection;
	}

	static getConnectionExtra(source: Source) {
		return {
			sourceId: +source.id!,
			sourceTypeKey: source.sourceType?.key,
			sourceTitle: source.title,
			connectionId: source.connection?.id?.toString(),
		};
	}
}
